import React from "react";
import { useTranslation } from "react-i18next";
import { getColorHex } from "../../constants/bomPricing";

interface BOMData {
  totalBricks: number;
  totalCost: number;
  colorSummary: { [key: string]: number };
}

interface BOMSummaryProps {
  bomData: BOMData;
  showCosts: boolean;
  formatCurrency: (amount: number) => string;
  buildingStyle?: "lego" | "minecraft";
}

const BOMSummary: React.FC<BOMSummaryProps> = ({
  bomData,
  showCosts,
  formatCurrency,
  buildingStyle = "lego",
}) => {
  const { t } = useTranslation();
  // Stroke icons (no emoji); the style switch picks the brick vs block glyph
  const BrickIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="9" width="18" height="10" rx="1.5" />
      <path d="M7 9V6.5A1.5 1.5 0 0 1 8.5 5h1A1.5 1.5 0 0 1 11 6.5V9M13 9V6.5A1.5 1.5 0 0 1 14.5 5h1A1.5 1.5 0 0 1 17 6.5V9" />
    </svg>
  );
  const BlockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <rect x="8.5" y="8.5" width="7" height="7" rx="1" />
    </svg>
  );
  const CostIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v9M14.75 9.25c-.5-.9-1.5-1.25-2.75-1.25-1.4 0-2.5.7-2.5 2s1.1 1.75 2.5 2 2.75.7 2.75 2-1.1 2-2.75 2c-1.25 0-2.25-.35-2.75-1.25" />
    </svg>
  );
  const PaletteIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.5 0 2-1 1.5-2s-.5-2 1-2H17a3.5 3.5 0 0 0 3.5-3.5C20.5 7 16.7 3.5 12 3.5Z" />
      <circle cx="8" cy="10" r="0.75" fill="currentColor" />
      <circle cx="12" cy="7.5" r="0.75" fill="currentColor" />
      <circle cx="16" cy="10" r="0.75" fill="currentColor" />
      <circle cx="8" cy="14.5" r="0.75" fill="currentColor" />
    </svg>
  );
  const icon = buildingStyle === "minecraft" ? <BlockIcon /> : <BrickIcon />;
  const label = buildingStyle === "minecraft" ? "Total Blocks" : t("bom.totalPieces");

  return (
    <div className="bom-summary">
      <div className="summary-cards">
        <div className="summary-card total-bricks">
          <div className="card-icon">{icon}</div>
          <div className="card-content">
            <h4>{bomData.totalBricks.toLocaleString()}</h4>
            <p>{label}</p>
          </div>
        </div>

        {showCosts && (
          <div className="summary-card total-cost">
            <div className="card-icon"><CostIcon /></div>
            <div className="card-content">
              <h4>{formatCurrency(bomData.totalCost)}</h4>
              <p>{t("bom.estimatedCost")}</p>
            </div>
          </div>
        )}

        <div className="summary-card unique-colors">
          <div className="card-icon"><PaletteIcon /></div>
          <div className="card-content">
            <h4>{Object.keys(bomData.colorSummary).length}</h4>
            <p>{t("bom.uniqueColors")}</p>
          </div>
        </div>
      </div>

      <div className="color-breakdown">
        <h4>{t("bom.colorBreakdown")}</h4>
        <div className="color-bars">
          {Object.entries(bomData.colorSummary)
            .sort(([, a], [, b]) => b - a)
            .map(([color, count]) => (
              <div key={color} className="color-bar-item">
                <div className="color-info">
                  <span className="color-name">{color}</span>
                  <span className="color-count">
                    {count.toLocaleString()}
                  </span>
                </div>
                <div className="color-bar">
                  <div
                    className="color-bar-fill"
                    style={{
                      width: `${(count / bomData.totalBricks) * 100}%`,
                      backgroundColor: getColorHex(color),
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default BOMSummary;
